/**
 * Time Entry Form Component
 * L0-CMD-2026-0120-012 - Hours Logging System
 *
 * Allows drafters to log time against projects.
 */

import React, { useState, useEffect } from 'react';

interface Project {
  id: number;
  name: string;
  number: string;
  status?: string;
}

interface TimeEntryFormProps {
  projects?: Project[];
  onSubmit: (entry: TimeEntryData) => Promise<void>;
  initialDate?: Date;
  onCancel?: () => void;
}

interface TimeEntryData {
  project_id: number;
  date: string;
  hours: number;
  task_type: string;
  description?: string;
  is_billable: boolean;
}

const TASK_TYPES = [
  { value: 'drafting', label: 'Drafting', color: '#2563eb' },
  { value: 'review', label: 'Review', color: '#7c3aed' },
  { value: 'coordination', label: 'Coordination', color: '#059669' },
  { value: 'revision', label: 'Revision', color: '#d97706' },
  { value: 'admin', label: 'Admin', color: '#6b7280' },
];

export function TimeEntryForm({ projects = [], onSubmit, initialDate, onCancel }: TimeEntryFormProps) {
  const [projectId, setProjectId] = useState<string>('');
  const [date, setDate] = useState(initialDate || new Date());
  const [hours, setHours] = useState('');
  const [taskType, setTaskType] = useState('drafting');
  const [description, setDescription] = useState('');
  const [isBillable, setIsBillable] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!projectId || !hours) {
      setError('Please select a project and enter hours');
      return;
    }

    const hoursNum = parseFloat(hours);
    if (isNaN(hoursNum) || hoursNum <= 0 || hoursNum > 24) {
      setError('Hours must be between 0 and 24');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({
        project_id: parseInt(projectId),
        date: date.toISOString().split('T')[0],
        hours: hoursNum,
        task_type: taskType,
        description: description || undefined,
        is_billable: isBillable,
      });

      // Reset form
      setHours('');
      setDescription('');
    } catch (err: any) {
      setError(err.message || 'Failed to log time');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (d: Date) => d.toISOString().split('T')[0];

  return (
    <form className="time-entry-form" onSubmit={handleSubmit}>
      <style>{`
        .time-entry-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          padding: 1rem;
          background: #f8fafc;
          border-radius: 8px;
        }
        .form-row {
          display: flex;
          gap: 1rem;
          align-items: flex-end;
        }
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          flex: 1;
        }
        .form-group.hours {
          max-width: 100px;
        }
        .form-group label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
        }
        .form-group select,
        .form-group input[type="date"],
        .form-group input[type="number"],
        .form-group input[type="text"] {
          padding: 0.5rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
        }
        .form-group select:focus,
        .form-group input:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
        }
        .task-buttons {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .task-btn {
          padding: 0.375rem 0.75rem;
          border: 2px solid #e5e7eb;
          border-radius: 6px;
          background: white;
          font-size: 0.75rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .task-btn:hover {
          border-color: #9ca3af;
        }
        .task-btn.active {
          color: white;
        }
        .footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 0.5rem;
        }
        .billable-toggle {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          font-size: 0.875rem;
        }
        .billable-toggle input {
          width: 16px;
          height: 16px;
        }
        .button-group {
          display: flex;
          gap: 0.5rem;
        }
        .submit-btn {
          padding: 0.5rem 1rem;
          background: #2563eb;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.15s ease;
        }
        .submit-btn:hover:not(:disabled) {
          background: #1d4ed8;
        }
        .submit-btn:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }
        .cancel-btn {
          padding: 0.5rem 1rem;
          background: #f3f4f6;
          color: #374151;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
        }
        .cancel-btn:hover {
          background: #e5e7eb;
        }
        .error-message {
          color: #dc2626;
          font-size: 0.875rem;
          padding: 0.5rem;
          background: #fef2f2;
          border-radius: 4px;
        }
      `}</style>

      {error && <div className="error-message">{error}</div>}

      <div className="form-row">
        <div className="form-group">
          <label>Project</label>
          <select
            value={projectId}
            onChange={e => setProjectId(e.target.value)}
            required
          >
            <option value="">Select project...</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>
                {p.number} - {p.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Date</label>
          <input
            type="date"
            value={formatDate(date)}
            onChange={e => setDate(new Date(e.target.value + 'T00:00:00'))}
            required
          />
        </div>

        <div className="form-group hours">
          <label>Hours</label>
          <input
            type="number"
            step="0.25"
            min="0.25"
            max="24"
            value={hours}
            onChange={e => setHours(e.target.value)}
            placeholder="0.00"
            required
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Task Type</label>
          <div className="task-buttons">
            {TASK_TYPES.map(type => (
              <button
                key={type.value}
                type="button"
                className={`task-btn ${taskType === type.value ? 'active' : ''}`}
                style={{
                  borderColor: taskType === type.value ? type.color : undefined,
                  backgroundColor: taskType === type.value ? type.color : undefined,
                }}
                onClick={() => setTaskType(type.value)}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Description (optional)</label>
          <input
            type="text"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="What did you work on?"
          />
        </div>
      </div>

      <div className="form-row footer">
        <label className="billable-toggle">
          <input
            type="checkbox"
            checked={isBillable}
            onChange={e => setIsBillable(e.target.checked)}
          />
          <span>Billable</span>
        </label>

        <div className="button-group">
          {onCancel && (
            <button type="button" className="cancel-btn" onClick={onCancel}>
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="submit-btn"
            disabled={submitting || !projectId || !hours}
          >
            {submitting ? 'Saving...' : 'Log Time'}
          </button>
        </div>
      </div>
    </form>
  );
}

export default TimeEntryForm;
